import { CustomerProfitByYear } from "../../../fms.core/BusinessIntelligenceModel";
import { cbmToCft, kgsToLbs } from "../../../util";
import { openLoadingWindow, renderReportIntoWindow, buildBroadcastHyperlink, fmtAsOf } from "./CustomDashboardUtils";

const COLORS = {
  navy: "#0A1F33",
  gold: "#B8862E",
  goldLight: "#E4C88A",
  steelBlue: "#3E6E96",
  up: "#2F6F52",
  down: "#9C3B3B",
  textMuted: "#4A5A66",
  faint: "#8A98A2",
  border: "#D8E0E3",
  rowAlt: "#FAFBFB",
  subTotalBg: "#E4EDF4",
  subTotalLine: "#A9C6DC",
  totalBg: "#FBF6EA",

  link: "#1668DC",
  linkHover: "#0D4FA8",
};

const YEAR_COUNT = 5;
/** period 1 = 최근 연도(이번 연도) */
const CURRENT_PERIOD = 1;
const OP_ORDER = ["Ocean Import", "Ocean Export", "Air Import", "Air Export", "Other Shipment", "Warehouse"];

const STYLE = `
  @page { size: A4 portrait; margin: 13mm 12mm 15mm; }
  * { box-sizing: border-box; }
  body { font-family:-apple-system,'Segoe UI',Pretendard,Arial,sans-serif; color:#101B26; margin:0; font-size:11.5px; background:#fff; }
  .cp-report { max-width:960px; margin:0 auto; }

  .masthead { background:${COLORS.navy}; color:#fff; padding:18px 24px 16px; border-radius:5px;
              display:flex; justify-content:space-between; align-items:flex-end; margin-bottom:12px; }
  .masthead h1 { font-size:21px; margin:0 0 5px; font-weight:700; letter-spacing:-.01em; }
  .masthead .eyebrow { font-size:9.5px; letter-spacing:.14em; text-transform:uppercase; color:${COLORS.goldLight}; margin-bottom:6px; }
  .masthead .meta { font-size:10px; color:#B9C7D1; line-height:1.6; }
  .masthead .meta b { color:#fff; }
  .masthead .right { text-align:right; font-size:9.5px; color:#8FA7B8; line-height:1.5; }
  .masthead .cust-name { font-size:12px; color:#fff; font-weight:700; margin-top:2px; }

  /* KPI */
  .kpi-cards { display:flex; gap:10px; margin-bottom:12px; }
  .kpi-card { flex:1; background:#fff; border:1px solid ${COLORS.border}; border-top:3px solid ${COLORS.gold};
              border-radius:5px; padding:10px 12px 9px; display:flex; flex-direction:column; }
  .kpi-card.navy { border-top-color:${COLORS.navy}; }
  .kpi-card.blue { border-top-color:${COLORS.steelBlue}; }
  .kpi-label { font-size:9px; color:${COLORS.textMuted}; text-transform:uppercase; letter-spacing:.04em; font-weight:700; margin-bottom:5px; }
  .kpi-value { font-family:'Courier New',monospace; font-size:19px; font-weight:700; line-height:1; margin-bottom:5px; color:${COLORS.navy}; }
  .kpi-desc { font-size:9px; color:${COLORS.textMuted}; line-height:1.5; margin-bottom:8px; }

  /* KPI — 이번 연도: 실선 구분 + 카드 색 라벨 + 약간 큰 숫자. 라벨 색은 카드 윗선 색과 맞춘다. */
  .kpi-sub { margin-top:auto; padding-top:7px; border-top:1px solid ${COLORS.border};
             display:flex; justify-content:space-between; align-items:baseline; gap:8px; }
  .kpi-sub-label { font-size:9px; font-weight:800; color:${COLORS.gold}; text-transform:uppercase;
                   letter-spacing:.05em; white-space:nowrap; }
  .kpi-card.navy .kpi-sub-label { color:${COLORS.navy}; }
  .kpi-card.blue .kpi-sub-label { color:${COLORS.steelBlue}; }
  .kpi-sub-value { font-family:'Courier New',monospace; font-size:14px; font-weight:700; line-height:1.1;
                   color:${COLORS.navy}; white-space:nowrap; flex-shrink:0; text-align:right; }
  .kpi-sub-value.neg { color:${COLORS.down}; }
  /* 금액 바로 옆, 같은 줄에 "/ 비중%" */
  .kpi-sub-note { font-family:-apple-system,'Segoe UI',Pretendard,Arial,sans-serif;
                  font-size:9px; color:${COLORS.textMuted}; font-weight:700; margin-left:5px; }
  /* Profit 카드 — 이번 연도 마진은 다음 줄, 오른쪽 정렬 */
  .kpi-sub-margin { display:block; margin-top:3px;
                    font-family:-apple-system,'Segoe UI',Pretendard,Arial,sans-serif;
                    font-size:9px; color:${COLORS.textMuted}; font-weight:700; }
  /* 마진 줄이 없는 카드에도 같은 높이의 빈 줄을 둬서, 세 카드의 구분선 높이를 맞춘다 */
  .kpi-sub-spacer { visibility:hidden; }

  /* Profit 카드 — 5개년 마진 줄: 배지 없이 숫자에만 good/mid/bad 색 */
  .kpi-margin { display:flex; align-items:baseline; gap:5px; margin-bottom:4px; }
  .kpi-margin-label { font-size:9px; font-weight:700; color:${COLORS.textMuted}; text-transform:uppercase; letter-spacing:.04em; }
  .kpi-margin-value { font-family:'Courier New',monospace; font-size:11px; font-weight:700; color:${COLORS.textMuted}; }
  .m-good { color:${COLORS.up}; }
  .m-mid  { color:#A35A00; }
  .m-bad  { color:${COLORS.down}; }

  /* Insight */
  .insight { background:${COLORS.navy}; color:#EAF0F4; border-radius:5px; padding:12px 14px; margin-bottom:12px; }
  .insight .lbl { font-family:'Courier New',monospace; font-size:8.5px; letter-spacing:.1em; text-transform:uppercase;
                  color:${COLORS.goldLight}; margin-bottom:5px; }
  .insight p { font-size:10px; line-height:1.65; margin:0; }
  .insight b { color:#fff; }

  /* Section heading */
  .section-head { display:flex; justify-content:space-between; align-items:baseline; margin-bottom:6px; margin-top:14px; }
  .section-title { font-size:12px; font-weight:700; color:${COLORS.navy}; text-transform:uppercase; letter-spacing:.03em; }
  .section-note { font-size:9px; color:${COLORS.textMuted}; font-weight:600; }

  /* 표 */
  table.op-table { width:100%; border-collapse:collapse; }
  table.op-table thead { display:table-header-group; }
  table.op-table th { background:${COLORS.navy}; color:#fff; font-size:8.3px; text-transform:uppercase; letter-spacing:.03em;
                      padding:5px 6px; text-align:right; font-weight:600; }
  table.op-table th.left-h { text-align:left; }
  table.op-table th.center-h { text-align:center; }
  table.op-table td { padding:4px 6px; text-align:right; border-bottom:1px solid #EEF2F0; vertical-align:middle;
                      font-family:'Courier New',monospace; font-size:9.3px; font-weight:700; }
  table.op-table td.left-c { text-align:left; font-family:-apple-system,'Segoe UI',Pretendard,Arial,sans-serif; font-size:9px; }
  table.op-table td.op-name { text-align:left; color:${COLORS.steelBlue}; font-weight:800; white-space:nowrap;
                              font-family:-apple-system,'Segoe UI',Pretendard,Arial,sans-serif; font-size:9px; }
  table.op-table td.yr { text-align:left; font-weight:700; color:${COLORS.navy}; white-space:nowrap; width:62px; font-size:9px; }
  table.op-table td.mh { width:30px; text-align:center; color:${COLORS.textMuted}; }
  table.op-table td.did { width:64px; text-align:center; white-space:nowrap; }
  table.op-table tr.yr-start td { border-top:1px solid ${COLORS.border}; }
  table.op-table tr { page-break-inside:avoid; }
  table.op-table tbody tr:nth-child(even) { background:${COLORS.rowAlt}; }

  /* D/ID 드릴다운 칩 */
  td.did a.drill-link,
  td.did a:link,
  td.did a:visited {
    display:inline-block; padding:1px 6px;
    border:1px solid ${COLORS.link}; border-radius:4px;
    background:#fff; color:${COLORS.link} !important;
    font-family:'Courier New',monospace; font-size:8.3px; font-weight:700; line-height:1.5;
    text-decoration:underline; text-underline-offset:2px; cursor:pointer;
    transition:background .15s ease, color .15s ease, border-color .15s ease;
  }
  td.did a.drill-link:hover {
    background:${COLORS.linkHover}; color:#fff !important; border-color:${COLORS.linkHover}; text-decoration:none;
  }
  td.did a * { color:inherit !important; text-decoration:inherit !important; }

  /* Operation 소계 */
  table.op-table tr.subtotal-row td { background:${COLORS.subTotalBg}; color:${COLORS.navy}; font-weight:800;
                                      border-top:1px solid ${COLORS.subTotalLine}; border-bottom:1px solid ${COLORS.subTotalLine}; }
  table.op-table tr.subtotal-row td:first-child { border-left:3px solid ${COLORS.steelBlue}; }

  /* Grand Total */
  table.op-table tr.total-row td { background:${COLORS.totalBg}; color:${COLORS.navy}; font-weight:800;
                                   border-top:2px solid ${COLORS.gold}; border-bottom:none; }

  /* D/ID 열의 Total / Grand Total 라벨 — 칩과 같은 가운데 정렬 */
  table.op-table tr.subtotal-row td.did,
  table.op-table tr.total-row td.did {
    text-align:center; white-space:nowrap;
    font-family:-apple-system,'Segoe UI',Pretendard,Arial,sans-serif;
    font-size:8.6px; font-weight:800; color:${COLORS.navy};
  }

  tfoot td { padding:8px 6px; font-family:'Courier New',monospace; font-weight:800; font-size:10px; color:${COLORS.navy};
             background:${COLORS.totalBg}; border-top:2px solid ${COLORS.gold}; border-bottom:none; text-align:right; }
  tfoot td.left-c { text-align:left; font-family:-apple-system,'Segoe UI',Pretendard,Arial,sans-serif; font-size:9.5px; }

  .badge { display:inline-block; padding:1px 7px; border-radius:8px; font-family:'Courier New',monospace;
           font-size:8.3px; font-weight:700; }
  .badge.good { background:${COLORS.up}22; color:${COLORS.up}; border:1px solid ${COLORS.up}55; }
  .badge.mid  { background:${COLORS.gold}22; color:#A35A00; border:1px solid ${COLORS.gold}55; }
  .badge.bad  { background:${COLORS.down}22; color:${COLORS.down}; border:1px solid ${COLORS.down}55; }
  .badge.none { background:${COLORS.border}44; color:${COLORS.faint}; border:1px solid ${COLORS.border}; }

  /* Shipment volume detail */
  .vol-wrap { display:grid; grid-template-columns:repeat(2, minmax(0, 1fr)); gap:12px; margin-bottom:8px; }
  .vol-card { background:#fff; border:1px solid ${COLORS.border}; border-top:3px solid ${COLORS.steelBlue};
              border-radius:5px; padding:11px 13px 12px; page-break-inside:avoid;
              min-width:0; overflow:hidden; }
  .vol-title { font-size:10.5px; font-weight:800; color:${COLORS.navy}; text-transform:uppercase; letter-spacing:.03em;
               margin-bottom:7px; padding-bottom:5px; border-bottom:1px solid ${COLORS.border}; }
  table.vol-table { width:100%; border-collapse:collapse; table-layout:fixed; }
  table.vol-table th { font-size:8px; font-weight:700; color:${COLORS.textMuted}; text-transform:uppercase; letter-spacing:.03em;
                       padding:3px 4px; text-align:right; white-space:nowrap; overflow:hidden; }
  table.vol-table th.left-h { width:62px; text-align:left; }
  table.vol-table td { font-family:'Courier New',monospace; font-size:8.7px; font-weight:700; padding:3px 4px;
                       text-align:right; border-top:1px solid #EFF3F2; white-space:nowrap;
                       overflow:hidden; text-overflow:ellipsis; }
  table.vol-table td.lbl { text-align:left; font-family:-apple-system,'Segoe UI',Pretendard,Arial,sans-serif;
                           font-size:8px; font-weight:800; color:${COLORS.textMuted};
                           text-transform:uppercase; letter-spacing:.3px; width:62px; }
  table.vol-table td.zero { color:#C7D0D4; }

  .footnote { font-size:8.5px; color:${COLORS.faint}; margin-top:10px; line-height:1.6;
              border-top:1px solid ${COLORS.border}; padding-top:6px; display:flex; justify-content:space-between; }

  .state-wrap { min-height:70vh; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:16px; }
  .spinner { width:40px; height:40px; border-radius:50%; border:4px solid #eee; border-top-color:${COLORS.gold}; animation:cp-spin 0.8s linear infinite; }
  @keyframes cp-spin { to { transform:rotate(360deg); } }
  .state-text { color:${COLORS.faint}; font-size:13px; }
  .state-text.error { color:${COLORS.down}; }

  @media print {
    .masthead, .insight, .kpi-card, .badge, tfoot td, tr.subtotal-row td, tr.total-row td, td.did a {
      -webkit-print-color-adjust:exact; print-color-adjust:exact;
    }
  }
`;

/**
 * 표시 자릿수로 반올림(0 에서 먼 쪽, toLocaleString 과 같은 방향). -0 은 0 으로.
 * 금액 합계는 부동소수 오차로 -7.1e-15 같은 값이 나올 수 있다(예: 10.1+20.2+30.3 - (30.3+20.2+10.1)).
 * 그대로 찍으면 "-0.00" 이 되고, 음수 판정(빨간색·"Net loss"·bad 배지)도 틀어지므로
 * 부호·색 판정도 이 함수로 반올림한 값으로 한다.
 */
function roundTo(n: number, digits: number): number {
  const f = Math.pow(10, digits);
  return (Math.sign(n) * Math.round(Math.abs(n) * f)) / f || 0;
}
function fmtDec(n: number, digits: number): string {
  return roundTo(n, digits).toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });
}
function fmtMoney(n: number): string {
  return fmtDec(n, 2);
}
function fmtPct(n: number): string {
  return fmtDec(n, 2) + "%";
}
function fmtInt(n: number): string {
  return fmtDec(n, 0);
}
/** 금액이 화면에 보이는 값(센트 단위) 기준으로 음수인가 */
function isNegMoney(n: number): boolean {
  return roundTo(n, 2) < 0;
}
function toNum(v: unknown): number {
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}
/** DB에서 온 문자열(고객명, 회사명 등)에 & < > " ' 가 있어도 HTML 이 깨지지 않도록 */
function escapeHtml(v: unknown): string {
  return String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function classifyMargin(margin: number): "good" | "mid" | "bad" {
  // 표시값(소수 2자리) 기준 — "0.00%" 가 bad 로, "3.00%" 가 mid 로 칠해지지 않게
  const m = roundTo(margin, 2);
  if (m < 0) return "bad";
  if (m >= 3) return "good";
  return "mid";
}
function marginBadge(margin: number | null): string {
  if (margin === null) return `<span class="badge none">&ndash;</span>`;
  return `<span class="badge ${classifyMargin(margin)}">${fmtPct(margin)}</span>`;
}
function fmtMarginText(margin: number | null): string {
  return margin === null ? "n/a" : fmtPct(margin);
}

function mapOperation(tbName: string): { operation: string; operationIndex: number } {
  switch (tbName) {
    case "T_OIMMAIN":
    case "T_OIHMAIN":
      return { operation: "Ocean Import", operationIndex: 0 };
    case "T_OOMMAIN":
    case "T_OOHMAIN":
      return { operation: "Ocean Export", operationIndex: 1 };
    case "T_AIMMAIN":
    case "T_AIHMAIN":
      return { operation: "Air Import", operationIndex: 2 };
    case "T_AOMMAIN":
    case "T_AOHMAIN":
      return { operation: "Air Export", operationIndex: 3 };
    case "T_GENMAIN":
      return { operation: "Other Shipment", operationIndex: 4 };
    default:
      return { operation: "Warehouse", operationIndex: 5 };
  }
}

function mapShipmentType(shipmentType: string): string {
  return shipmentType === "I" ? "ID" : shipmentType;
}

function computeProfit(sales: number, cost: number, agentSales: number, agentCost: number): number {
  return (sales + agentSales) - (cost + agentCost);
}
/** 분모(Invoice + Db Note)가 0 이하이면 마진을 정의할 수 없으므로 null. (비용만 있는 행에서 0.00% 로 보이던 문제) */
function computeMargin(sales: number, agentSales: number, profit: number): number | null {
  const base = sales + agentSales;
  // 센트 단위로 0 인 분모(부동소수 오차 1e-15 등)도 0 으로 본다 — 그대로 나누면 "1,000,000,000,000.00%" 같은 마진이 나온다
  return roundTo(base, 2) <= 0 ? null : (profit / base) * 100;
}

interface EnrichedRow {
  raw: CustomerProfitByYear;
  operation: string;
  operationIndex: number;
  shipmentTypeDisplay: string;
  period: number;
}

interface OpGroup {
  operation: string;
  rows: EnrichedRow[];
}

function enrichAndGroup(data: CustomerProfitByYear[]): OpGroup[] {
  const enriched: EnrichedRow[] = data.map(r => {
    const { operation, operationIndex } = mapOperation(r.tbName);
    return {
      raw: r,
      operation,
      operationIndex,
      shipmentTypeDisplay: mapShipmentType(r.shipmentType),
      period: toNum(r.period),
    };
  });

  enriched.sort((a, b) => {
    if (a.operationIndex !== b.operationIndex) return a.operationIndex - b.operationIndex;
    if (a.period !== b.period) return a.period - b.period; // 1 = 최근 연도
    return toNum(a.raw.viewOrder) - toNum(b.raw.viewOrder);
  });

  const groups: OpGroup[] = [];
  OP_ORDER.forEach((op, idx) => {
    const rows = enriched.filter(r => r.operationIndex === idx);
    if (rows.length > 0) groups.push({ operation: op, rows });
  });
  return groups;
}

function isOceanOp(op: string): boolean { return op.startsWith("Ocean"); }
function isAirOp(op: string): boolean { return op.startsWith("Air"); }
function isOtherOp(op: string): boolean { return op === "Other Shipment"; }
function isWarehouseOp(op: string): boolean { return op === "Warehouse"; }

/** 금액(Invoice/Bill/Db/Cr)이 하나라도 있는 행인가. 물량만 나르는 MBL 행(금액 전부 0)은 표에서 숨긴다. */
function hasMoney(r: CustomerProfitByYear): boolean {
  return toNum(r.sales) !== 0 || toNum(r.cost) !== 0 || toNum(r.agentSales) !== 0 || toNum(r.agentCost) !== 0;
}

function groupSum(rows: EnrichedRow[], f: (r: CustomerProfitByYear) => number): number {
  return rows.reduce((s, r) => s + toNum(f(r.raw)), 0);
}

/** Ocean/Air 행의 Master + House B/L 건수 합계 */
function sumBL(rows: EnrichedRow[]): number {
  return groupSum(
    rows.filter(r => isOceanOp(r.operation) || isAirOp(r.operation)),
    r => toNum(r.mCount) + toNum(r.hCount)
  );
}

function buildPeriods(rows: EnrichedRow[], asOfDate: Date): { list: number[]; labels: Map<number, string> } {
  const list = Array.from(new Set(rows.map(r => r.period))).sort((a, b) => a - b);
  const labels = new Map<number, string>();
  const baseYear = asOfDate.getFullYear();
  // period 0(5-Year Total)이 섞여 있어도 최근 연도는 0 을 제외한 가장 작은 period
  const newest = list.find(p => p > 0) ?? 0;

  list.forEach(p => {
    if (p === 0) { labels.set(p, "5-Year Total"); return; }
    const sample = rows.find(r => r.period === p)?.raw;
    const from = sample?.periodFrom ?? "";
    const to = sample?.periodTo ?? "";
    const year = /^\d{4}/.test(from) ? Number(from.slice(0, 4)) : baseYear - (p - 1);
    const partial = p === newest && /^\d{4}-\d{2}-\d{2}/.test(to) && to.slice(5, 10) !== "12-31";
    labels.set(p, partial ? `${year} YTD` : String(year));
  });
  return { list, labels };
}

/** 이번 연도 라벨. 이번 연도 행이 없으면 asOfDate 기준으로 만든다. */
function currentYearLabel(labels: Map<number, string>, asOfDate: Date): string {
  const fromData = labels.get(CURRENT_PERIOD);
  if (fromData) return fromData;
  const isYearEnd = asOfDate.getMonth() === 11 && asOfDate.getDate() === 31;
  return isYearEnd ? String(asOfDate.getFullYear()) : `${asOfDate.getFullYear()} YTD`;
}

function buildOperationsTableBody(
  groups: OpGroup[],
  labels: Map<number, string>,
  channelName: string,
  windowName: string
): string {
  return groups
    .map(({ operation, rows }) => {
      let lastPeriod: number | null = null;

      // 금액이 전부 0인 행(주로 HBL 에 연결된 MBL)은 표에서만 숨긴다.
      // 소계·Grand Total·Year Summary·물량 카드·B/L 건수는 전체 행으로 계산하므로 숫자는 바뀌지 않는다.
      const visibleRows = rows.filter(r => hasMoney(r.raw));

      const bodyRows = visibleRows
        .map(({ raw, shipmentTypeDisplay, period }, idx) => {
          const sales = toNum(raw.sales);
          const cost = toNum(raw.cost);
          const agentSales = toNum(raw.agentSales);
          const agentCost = toNum(raw.agentCost);
          const profit = computeProfit(sales, cost, agentSales, agentCost);
          const margin = computeMargin(sales, agentSales, profit);

          const didHtml = buildBroadcastHyperlink(shipmentTypeDisplay, channelName, [
            "CompanySalesSummaryLink",
            raw.tbName,
            raw.ids,
          ], windowName);

          const isNewYear = period !== lastPeriod;
          lastPeriod = period;

          const opNameCell = idx === 0 ? `<td class="op-name">${operation}</td>` : `<td></td>`;
          const yearCell = isNewYear ? `<td class="yr">${labels.get(period) ?? period}</td>` : `<td></td>`;

          return `
            <tr${isNewYear && idx > 0 ? ` class="yr-start"` : ""}>
              ${opNameCell}
              ${yearCell}
              <td class="mh">${escapeHtml(raw.mhType)}</td>
              <td class="did">${didHtml}</td>
              <td>${fmtMoney(sales)}</td>
              <td>${fmtMoney(cost)}</td>
              <td>${fmtMoney(agentSales)}</td>
              <td>${fmtMoney(agentCost)}</td>
              <td>${fmtMoney(profit)}</td>
              <td>${marginBadge(margin)}</td>
            </tr>`;
        })
        .join("");

      const sSales = groupSum(rows, r => r.sales);
      const sCost = groupSum(rows, r => r.cost);
      const sAgentSales = groupSum(rows, r => r.agentSales);
      const sAgentCost = groupSum(rows, r => r.agentCost);
      const sProfit = computeProfit(sSales, sCost, sAgentSales, sAgentCost);
      const sMargin = computeMargin(sSales, sAgentSales, sProfit);

      // Operation 소계 — 이름 칸은 비우고 D/ID 열에 "Total".
      const subtotalRow = `
        <tr class="subtotal-row">
          <td></td>
          <td></td>
          <td></td>
          <td class="did">Total</td>
          <td>${fmtMoney(sSales)}</td>
          <td>${fmtMoney(sCost)}</td>
          <td>${fmtMoney(sAgentSales)}</td>
          <td>${fmtMoney(sAgentCost)}</td>
          <td>${fmtMoney(sProfit)}</td>
          <td>${marginBadge(sMargin)}</td>
        </tr>`;

      return bodyRows + subtotalRow;
    })
    .join("");
}

function buildYearSummaryTable(
  allRows: EnrichedRow[],
  periods: number[],
  labels: Map<number, string>
): string {
  const perYear = periods.map(p => {
    const r = allRows.filter(x => x.period === p);
    const sales = groupSum(r, x => x.sales);
    const cost = groupSum(r, x => x.cost);
    const agentSales = groupSum(r, x => x.agentSales);
    const agentCost = groupSum(r, x => x.agentCost);
    const profit = computeProfit(sales, cost, agentSales, agentCost);
    return { p, sales, cost, agentSales, agentCost, profit, margin: computeMargin(sales, agentSales, profit) };
  });

  const body = perYear
    .map(y => {
      return `
        <tr>
          <td class="yr">${labels.get(y.p) ?? y.p}</td>
          <td>${fmtMoney(y.sales)}</td>
          <td>${fmtMoney(y.cost)}</td>
          <td>${fmtMoney(y.agentSales)}</td>
          <td>${fmtMoney(y.agentCost)}</td>
          <td>${fmtMoney(y.profit)}</td>
          <td>${marginBadge(y.margin)}</td>
        </tr>`;
    })
    .join("");

  const tSales = groupSum(allRows, r => r.sales);
  const tCost = groupSum(allRows, r => r.cost);
  const tAgentSales = groupSum(allRows, r => r.agentSales);
  const tAgentCost = groupSum(allRows, r => r.agentCost);
  const tProfit = computeProfit(tSales, tCost, tAgentSales, tAgentCost);

  return `
    <table class="op-table">
      <thead>
        <tr>
          <th class="left-h">Year</th>
          <th>Invoice</th><th>Bill</th><th>Db Note</th><th>Cr Note</th><th>Profit</th><th>Margin</th>
        </tr>
      </thead>
      <tbody>${body}</tbody>
      <tfoot>
        <tr>
          <td class="left-c">TOTAL</td>
          <td>${fmtMoney(tSales)}</td>
          <td>${fmtMoney(tCost)}</td>
          <td>${fmtMoney(tAgentSales)}</td>
          <td>${fmtMoney(tAgentCost)}</td>
          <td>${fmtMoney(tProfit)}</td>
          <td>${marginBadge(computeMargin(tSales, tAgentSales, tProfit))}</td>
        </tr>
      </tfoot>
    </table>`;
}

interface VolMetric {
  label: string;
  get: (r: CustomerProfitByYear) => number;
  fmt: (n: number) => string;
}

function volMetrics(operation: string): VolMetric[] {
  if (isOceanOp(operation)) {
    return [
      { label: "MBL", get: r => r.mCount, fmt: fmtInt },
      { label: "HBL", get: r => r.hCount, fmt: fmtInt },
      { label: "CBM", get: r => r.rTonCbm, fmt: n => fmtDec(n, 3) },
      { label: "20FT", get: r => r.rTon20, fmt: fmtInt },
      { label: "40FT", get: r => r.rTon40, fmt: fmtInt },
      { label: "45FT", get: r => r.rTon45, fmt: fmtInt },
      { label: "HQ", get: r => r.rTon40H, fmt: fmtInt },
    ];
  }
  if (isAirOp(operation)) {
    return [
      { label: "MAWB", get: r => r.mCount, fmt: fmtInt },
      { label: "HAWB", get: r => r.hCount, fmt: fmtInt },
      { label: "Gross Wt", get: r => r.gWeight, fmt: fmtInt },
      { label: "Chg Wt", get: r => r.cWeight, fmt: fmtInt },
    ];
  }
  if (isOtherOp(operation)) {
    return [
      { label: "OT", get: r => r.otCount, fmt: fmtInt },
      { label: "Pkgs", get: r => r.rTonPkgs, fmt: fmtInt },
      { label: "Kgs", get: r => r.rTonKgs, fmt: n => fmtDec(n, 2) },
      { label: "LBS", get: r => kgsToLbs(toNum(r.rTonKgs)), fmt: n => fmtDec(n, 2) },
      { label: "CBM", get: r => r.rTonCbm, fmt: n => fmtDec(n, 3) },
      { label: "CFT", get: r => cbmToCft(toNum(r.rTonCbm)), fmt: n => fmtDec(n, 2) },
    ];
  }
  if (isWarehouseOp(operation)) {
    return [
      { label: "WHR", get: r => r.whrCount, fmt: fmtInt },
      { label: "WHS", get: r => r.whsCount, fmt: fmtInt },
      { label: "Period Inv", get: r => r.pInvCount, fmt: fmtInt },
      { label: "Period CrDb", get: r => r.pCrDbCount, fmt: fmtInt },
      { label: "Storage Inv", get: r => r.sInvCount, fmt: fmtInt },
    ];
  }
  return [];
}

function buildVolCard(
  operation: string,
  rows: EnrichedRow[],
  periods: number[],
  labels: Map<number, string>
): string {
  const metrics = volMetrics(operation);
  if (metrics.length === 0) return "";

  const head = periods.map(p => `<th>${labels.get(p) ?? p}</th>`).join("");

  const body = metrics
    .map(mt => {
      const cells = periods
        .map(p => {
          const v = groupSum(rows.filter(r => r.period === p), mt.get);
          const text = mt.fmt(v);
          // 합계가 0 이거나, 표시 자릿수에서 0 이 되는 값(예: 5.5e-17 → "0.000")은 "–"
          if (v === 0 || /^0(\.0+)?$/.test(text)) return `<td class="zero">–</td>`;
          return `<td title="${mt.label}: ${text}">${text}</td>`;
        })
        .join("");
      return `<tr><td class="lbl">${mt.label}</td>${cells}</tr>`;
    })
    .join("");

  return `
    <div class="vol-card">
      <div class="vol-title">${operation}</div>
      <table class="vol-table">
        <thead><tr><th class="left-h"></th>${head}</tr></thead>
        <tbody>${body}</tbody>
      </table>
    </div>`;
}

function buildReportInnerHtml(
  data: CustomerProfitByYear[],
  asOfDate: Date,
  companyName: string,
  currency: string,
  department: string,
  comSName: string,
  channelName: string,
  windowName: string,
  footerLeft?: string,
  footerRight?: string
): string {
  if (data.length === 0) {
    return `<div class="cp-report"><p style="padding:24px;color:${COLORS.faint};">No data available.</p></div>`;
  }

  const groups = enrichAndGroup(data);
  const allRows = groups.flatMap(g => g.rows);
  const { list: periods, labels } = buildPeriods(allRows, asOfDate);
  const asOfStr = fmtAsOf(asOfDate);

  const grandSales = data.reduce((s, r) => s + toNum(r.sales), 0);
  const grandCost = data.reduce((s, r) => s + toNum(r.cost), 0);
  const grandAgentSales = data.reduce((s, r) => s + toNum(r.agentSales), 0);
  const grandAgentCost = data.reduce((s, r) => s + toNum(r.agentCost), 0);
  const grandProfit = computeProfit(grandSales, grandCost, grandAgentSales, grandAgentCost);
  const grandMargin = computeMargin(grandSales, grandAgentSales, grandProfit);
  /** Total Sales = Invoice + Db Note (마진 분모와 같은 기준) */
  const grandTotalSales = grandSales + grandAgentSales;

  const oceanRows = groups.filter(g => isOceanOp(g.operation)).flatMap(g => g.rows);
  const airRows = groups.filter(g => isAirOp(g.operation)).flatMap(g => g.rows);
  const oceanMbl = groupSum(oceanRows, r => r.mCount);
  const oceanHbl = groupSum(oceanRows, r => r.hCount);
  const airMawb = groupSum(airRows, r => r.mCount);
  const airHawb = groupSum(airRows, r => r.hCount);
  const totalBL = oceanMbl + oceanHbl + airMawb + airHawb;
  const blDescParts: string[] = [];
  if (oceanMbl + oceanHbl > 0) blDescParts.push(`${fmtInt(oceanMbl + oceanHbl)} MBL/HBL`);
  if (airMawb + airHawb > 0) blDescParts.push(`${fmtInt(airMawb + airHawb)} MAWB/HAWB`);
  const blDesc = blDescParts.length > 0
    ? `${blDescParts.join(" + ")} across all operations, over ${YEAR_COUNT} years`
    : "No B/L volume recorded across active operations";

  const perYearSales = periods.map(p => {
    const r = allRows.filter(x => x.period === p);
    // 피크 연도도 KPI 와 같은 Sales(Invoice + Db Note) 기준
    return { p, sales: groupSum(r, x => toNum(x.sales) + toNum(x.agentSales)), active: r.some(x => hasMoney(x.raw)) };
  });
  const peak = perYearSales.reduce((a, b) => (b.sales > a.sales ? b : a), perYearSales[0]);
  const activeYears = perYearSales.filter(x => x.active).length;

  /* ── 이번 연도(period 1). 해당 연도 행이 없어도 0으로 표시 */
  const curRows = allRows.filter(r => r.period === CURRENT_PERIOD);
  const curLabel = currentYearLabel(labels, asOfDate);
  // KPI 카드 이번 연도 라벨: "2026 YTD / SHARE" (% 가 5개년 대비 비중임을 표시)
  const curSubLabel = `${curLabel} / Share`;
  const curSales = groupSum(curRows, r => r.sales);
  const curCost = groupSum(curRows, r => r.cost);
  const curAgentSales = groupSum(curRows, r => r.agentSales);
  const curAgentCost = groupSum(curRows, r => r.agentCost);
  const curProfit = computeProfit(curSales, curCost, curAgentSales, curAgentCost);
  const curTotalSales = curSales + curAgentSales;
  /** 이번 연도 값이 5개년 합계에서 차지하는 비중. 5개년 합계가 0 이하이면 비중이 의미 없으므로 표시하지 않는다. */
  const curSalesShare = grandTotalSales > 0 ? (curTotalSales / grandTotalSales) * 100 : null;
  const curProfitShare = grandProfit > 0 ? (curProfit / grandProfit) * 100 : null;
  const curMargin = computeMargin(curSales, curAgentSales, curProfit);
  // 금액 옆 "/ 비중%" — 비중을 계산할 수 없으면 생략
  const shareNote = (share: number | null): string =>
    share === null ? "" : `<span class="kpi-sub-note">/ ${fmtPct(share)}</span>`;
  // Profit 카드 이번 연도 마진 (다음 줄, 마진 숫자만 good/mid/bad 색)
  const subSpacer = `<span class="kpi-sub-margin kpi-sub-spacer" aria-hidden="true">&nbsp;</span>`;
  const curMarginLine = curMargin === null
    ? subSpacer
    : `<span class="kpi-sub-margin">Margin <span class="m-${classifyMargin(curMargin)}">${fmtPct(curMargin)}</span></span>`;
  const curBL = sumBL(curRows);
  const curBLShare = totalBL > 0 ? (curBL / totalBL) * 100 : null;

  /* ── Insight 문장 조각: 데이터에 따라 말이 안 되는 문구는 빼거나 바꾼다 */
  const custLabel = comSName && comSName.trim() !== "" ? escapeHtml(comSName) : "This customer";
  const profitText = !isNegMoney(grandProfit)
    ? `<b>${fmtMoney(grandProfit)}</b> in profit`
    : `a <b>loss of ${fmtMoney(-grandProfit)}</b>`;
  const marginText = grandMargin === null
    ? ""
    : `, for a blended margin of <b>${fmtMarginText(grandMargin)}</b>`;
  const blText = totalBL > 0
    ? `, supported by <b>${fmtInt(totalBL)} total bills of lading</b>`
    : "";
  const peakText = peak && peak.sales > 0
    ? `, with sales peaking in <b>${labels.get(peak.p)}</b> at <b>${fmtMoney(peak.sales)}</b>`
    : "";
  const activeText = activeYears > 0
    ? `The account was active in <b>${activeYears} of the last ${YEAR_COUNT} years</b>${peakText}.`
    : `No sales or billing was recorded in the last ${YEAR_COUNT} years.`;

  const yearSummaryTable = buildYearSummaryTable(allRows, periods, labels);
  const operationsTableBody = buildOperationsTableBody(groups, labels, channelName, windowName);
  const volCardsHtml = groups.map(({ operation, rows }) => buildVolCard(operation, rows, periods, labels)).join("");

  // 전체 합계 — tfoot 대신 tbody 마지막 줄(인쇄 시 페이지마다 반복되지 않도록).
  const grandTotalRow = `
    <tr class="total-row">
      <td></td>
      <td></td>
      <td></td>
      <td class="did">Grand Total</td>
      <td>${fmtMoney(grandSales)}</td>
      <td>${fmtMoney(grandCost)}</td>
      <td>${fmtMoney(grandAgentSales)}</td>
      <td>${fmtMoney(grandAgentCost)}</td>
      <td>${fmtMoney(grandProfit)}</td>
      <td>${marginBadge(grandMargin)}</td>
    </tr>`;

  return `
    <div class="cp-report">
      <div class="masthead">
        <div>
          <div class="eyebrow">IFMS 5.0 · Business Intelligence</div>
          <h1>SALES / VOLUME (B/L Based)</h1>
          <div class="cust-name">${custLabel} associated</div>
          <div class="meta">Type: ${escapeHtml(department)} | Currency: <b>${escapeHtml(currency)}</b></div>
        </div>
        <div class="right">
          ${escapeHtml(companyName)}<br>
          As of <b style="color:#fff">${asOfStr}</b> &middot; (5 Years, by year)
        </div>
      </div>

      <div class="kpi-cards">
        <div class="kpi-card">
          <div class="kpi-label">Total Sales (5-Yr)</div>
          <div class="kpi-value">${fmtMoney(grandTotalSales)}</div>
          <div class="kpi-desc">Invoice + Db Note across all active operations</div>
          <div class="kpi-sub">
            <span class="kpi-sub-label">${curSubLabel}</span>
            <span class="kpi-sub-value">${fmtMoney(curTotalSales)}${shareNote(curSalesShare)}${subSpacer}</span>
          </div>
        </div>
        <div class="kpi-card navy">
          <div class="kpi-label">Total Profit (5-Yr)</div>
          <div class="kpi-value">${fmtMoney(grandProfit)}</div>
          <div class="kpi-margin"><span class="kpi-margin-label">Margin</span>${grandMargin === null ? `<span class="kpi-margin-value">n/a</span>` : `<span class="kpi-margin-value m-${classifyMargin(grandMargin)}">${fmtPct(grandMargin)}</span>`}</div>
          <div class="kpi-desc">${isNegMoney(grandProfit) ? "Net loss" : "Net profit"} across all operations</div>
          <div class="kpi-sub">
            <span class="kpi-sub-label">${curSubLabel}</span>
            <span class="kpi-sub-value${isNegMoney(curProfit) ? " neg" : ""}">${fmtMoney(curProfit)}${shareNote(curProfitShare)}${curMarginLine}</span>
          </div>
        </div>
        <div class="kpi-card blue">
          <div class="kpi-label">Bills of Lading (5-Yr)</div>
          <div class="kpi-value">${fmtInt(totalBL)}</div>
          <div class="kpi-desc">${blDesc}</div>
          <div class="kpi-sub">
            <span class="kpi-sub-label">${curSubLabel}</span>
            <span class="kpi-sub-value">${fmtInt(curBL)}${shareNote(curBLShare)}${subSpacer}</span>
          </div>
        </div>
      </div>

      <div class="insight">
        <div class="lbl">Insight</div>
        <p>Over the past ${YEAR_COUNT} years, ${custLabel} generated <b>${fmtMoney(grandTotalSales)}</b> in sales
          (Invoice + Db Note) and
          ${profitText} across its active operations${marginText}${blText}.
          ${activeText} The year-by-year breakdown below shows how each operation moved in each period.</p>
      </div>

      <div class="section-head">
        <div class="section-title">Year Summary</div>
        <div class="section-note">All operations combined</div>
      </div>
      ${yearSummaryTable}

      <div class="section-head">
        <div class="section-title">Operations — Year by Year</div>
        <div class="section-note">M = Master, H = House · click a D/ID chip to drill down · B/Ls with no amounts are omitted (still counted in volumes)</div>
      </div>
      <table class="op-table">
        <thead>
          <tr>
            <th class="left-h">Operation</th>
            <th class="left-h">Year</th>
            <th class="center-h">M/H</th>
            <th class="center-h">D/ID</th>
            <th>Invoice</th><th>Bill</th><th>Db Note</th><th>Cr Note</th><th>Profit</th><th>Margin</th>
          </tr>
        </thead>
        <tbody>
          ${operationsTableBody}
          ${grandTotalRow}
        </tbody>
      </table>

      <div class="section-head">
        <div class="section-title">Shipment Volume Detail</div>
        <div class="section-note">Volumes by year</div>
      </div>
      <div class="vol-wrap">
        ${volCardsHtml}
      </div>

      <div class="footnote">
        <div>${footerLeft || "Source data: Sales/Volume Report (B/L Based) · Linked from Total Sales (Customer) report"}</div>
        <div>${footerRight || `Printed on ${asOfStr}`}</div>
      </div>
    </div>`;
}

/**
 * @param targetName 리포트 창의 See Detail 이 클릭 시점에 미리 열어 둔 탭 이름.
 *   있으면 새 팝업 대신 그 탭에 그린다 — 메인 창에서 새 팝업을 열면 사용자 클릭이 없어 브라우저가 차단한다.
 */
export function openCustomerProfitLoadingWindow(windowName: string, comSName: string, targetName?: string): Window | null {
  return openLoadingWindow({
    title: `SALES / VOLUME (B/L Based) - ${comSName}`,
    styleText: STYLE,
    windowName,
    targetName,
  });
}

export function renderCustomerProfit(
  win: Window,
  data: CustomerProfitByYear[],
  asOfDate: Date,
  companyName: string,
  currency: string,
  department: string,
  comSName: string,
  channelName: string,
  windowName: string,
  footerLeft?: string,
  footerRight?: string
): void {
  const html = buildReportInnerHtml(data, asOfDate, companyName, currency, department, comSName, channelName, windowName, footerLeft, footerRight);
  renderReportIntoWindow(win, html);
}